import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { useProviderHealth } from '../queries';
import type { ProviderHealth } from '../services';

/**
 * Compact, inline provider-health indicator.
 *
 * Replaces the old fixed bottom-left overlay (which floated over the canvas /
 * dashboard launchers and didn't reflow with the layout). This version is a
 * normal flow element — drop it into the AgentBar header's spare real estate.
 * It shows a single overall status dot + a terse summary; clicking opens the
 * full per-provider breakdown (latency, model count, errors) in a popover.
 *
 * Polls `/api/v3/providers/health` every 30s via `useProviderHealth`.
 */
export const ProviderStatusIndicator: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useProviderHealth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const providers: ProviderHealth[] = [
    ...(data?.llm ?? []),
    ...(data?.image ?? []),
  ];
  const overall = overallStatusFor(providers, isError);

  return (
    <>
      <Tooltip title={overall.tooltip}>
        <ButtonBase
          onClick={e => setAnchor(e.currentTarget)}
          aria-label="Provider status"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {isLoading ? (
            <CircularProgress size={10} />
          ) : (
            <CircleIcon sx={{ fontSize: 10, color: overall.color }} />
          )}
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: overall.labelColor }}>
            {overall.label}
          </Typography>
        </ButtonBase>
      </Tooltip>

      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 320, maxWidth: 480 } } }}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Provider Health</Typography>
            <IconButton size="small" onClick={() => refetch()} aria-label="Refresh now">
              <Typography variant="caption" color="primary">Refresh</Typography>
            </IconButton>
          </Stack>
          {isError && (
            <Typography variant="caption" color="error">
              Health check failed: {(error as Error | undefined)?.message ?? 'unknown error'}
            </Typography>
          )}
          {dataUpdatedAt > 0 && (
            <Typography variant="caption" color="text.secondary">
              Updated: {new Date(dataUpdatedAt).toLocaleTimeString()}{' '}
              {isFetching && <CircularProgress size={10} sx={{ ml: 0.5, verticalAlign: 'middle' }} />}
            </Typography>
          )}
          <Divider />
          {data?.llm.length ? <ProviderGroup title="Language Models" items={data.llm} /> : null}
          {data?.image.length ? <ProviderGroup title="Image Generation" items={data.image} /> : null}
          {(!data || providers.length === 0) && !isLoading && (
            <Typography variant="caption" color="text.secondary">No providers registered.</Typography>
          )}
        </Stack>
      </Popover>
    </>
  );
};

const ProviderGroup: React.FC<{ title: string; items: ProviderHealth[] }> = ({ title, items }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {title}
    </Typography>
    <Stack spacing={0.5}>
      {items.map(p => (
        <Stack direction="row" alignItems="center" spacing={1} key={`${p.kind}:${p.name}`}>
          <CircleIcon
            sx={{ fontSize: 10, color: p.status === 'ok' ? 'success.main' : 'error.main' }}
            aria-label={p.status}
          />
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }}>{p.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56, textAlign: 'right' }}>
            {p.latencyMs} ms
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'right' }}>
            {p.modelCount} models
          </Typography>
          {p.error && (
            <Tooltip title={p.error}>
              <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 80 }}>
                error
              </Typography>
            </Tooltip>
          )}
        </Stack>
      ))}
    </Stack>
  </Box>
);

function overallStatusFor(
  providers: ProviderHealth[],
  isError: boolean,
): { color: string; labelColor: string; label: string; tooltip: string } {
  if (isError) return { color: 'error.main', labelColor: 'error.main', label: 'Providers', tooltip: 'Health check failed' };
  if (providers.length === 0) return { color: 'text.disabled', labelColor: 'text.secondary', label: 'Providers', tooltip: 'Loading provider status…' };
  const downCount = providers.filter(p => p.status !== 'ok').length;
  if (downCount === 0) return { color: 'success.main', labelColor: 'text.secondary', label: 'Providers', tooltip: 'All providers healthy' };
  if (downCount === providers.length) return { color: 'error.main', labelColor: 'error.main', label: 'All down', tooltip: 'All providers down' };
  return { color: 'warning.main', labelColor: 'warning.main', label: `${downCount} down`, tooltip: `${downCount} provider(s) down` };
}
